import React from 'react'
import styled from 'styled-components'
import { Badge } from './Badge'

export type AbwClassroomGridCardProps = {
	title: string
	/** Instructor name — its own row under the title. */
	instructorName?: string
	/** Education types; the first shows, the rest collapse into a "+x" chip. */
	typeLabels?: string[]
	/** Education years; the first shows, the rest collapse into a "+y" chip. */
	yearLabels?: string[]
	/** Education sections/tracks — renders once the API provides them. */
	sectionLabels?: string[]
	/** Localized mode label for the gray badge (أونلاين / سنتر). */
	mode?: string
	/** Subject (category) name — a blue-tint chip beside the mode badge. */
	subjectLabel?: string
	/** Cover photo URL; when absent the tinted strip + icon/emoji shows. */
	photo?: string | null
	/** Category icon URL for the tinted fallback cover. */
	icon?: string | null
	/** Fallback emoji when there is no photo and no icon. */
	emoji?: string
	/** Tint color behind the fallback cover (any CSS color). */
	tint?: string
	/** Shows the success-tint "مشترك" badge (and hides the CTA button). */
	enrolled?: boolean
	enrolledLabel?: string
	/** Banned enrollment — shows the error-tint "محظور" badge instead. */
	banned?: boolean
	bannedLabel?: string
	/** Expired access (standalone courses) — gray "منتهي" badge; hides the CTA. */
	expired?: boolean
	expiredLabel?: string
	/** Price shown above the CTA button (once the API provides one). */
	priceText?: string
	/** CTA button label (اشترك); shown when not enrolled. Triggers onClick. */
	ctaLabel?: string
	/** Small yellow pills between the info rows and the bottom row (bundle
	 *  membership on course cards). Each chip's own onClick wins over the
	 *  card's — both pointer and keyboard activation are intercepted so the
	 *  chip navigates independently; max 2 show, the rest collapse to +n. */
	chips?: { key: string | number; label: string; onClick?: () => void }[]
	onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void
	className?: string
}

/** One info row: the first value + a "+x" chip for the collapsed rest. */
const OverflowRow: React.FC<{ values: string[] }> = ({ values }) => {
	if (!values.length) return null
	return (
		<InfoRow>
			<span className="v">{values[0]}</span>
			{values.length > 1 && <span className="more" dir="ltr">{`+${values.length - 1}`}</span>}
		</InfoRow>
	)
}

/**
 * The design's classroom browse card. The cover keeps a 2:1 aspect ratio so the
 * card stays proportional at every width (88px tall at the design's 176px
 * card). Rows: title · instructor · education type(s) · year(s) · bottom row.
 * Bottom row per the design: mode badge at the start; at the end a stacked
 * column of مشترك badge / price / اشترك button. The button repeats the card's
 * own onClick (same navigation) so enrolling stays one flow.
 */
export const ClassroomGridCard: React.FC<AbwClassroomGridCardProps> = ({
	title,
	instructorName,
	typeLabels = [],
	yearLabels = [],
	sectionLabels = [],
	mode,
	subjectLabel,
	photo,
	icon,
	emoji = '📚',
	tint = 'var(--blue-12)',
	enrolled,
	enrolledLabel = 'مشترك',
	banned,
	bannedLabel = 'محظور',
	expired,
	expiredLabel = 'منتهي',
	priceText,
	ctaLabel,
	chips,
	onClick,
	className,
}) => (
	<Card
		className={className}
		onClick={onClick}
		role={onClick ? 'button' : undefined}
		tabIndex={onClick ? 0 : undefined}
		onKeyDown={
			onClick
				? e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							onClick(e)
						}
				  }
				: undefined
		}
	>
		<Cover style={photo ? undefined : { background: tint }}>
			{photo ? <img className="photo" src={photo} alt="" /> : icon ? <img className="icon" src={icon} alt="" /> : <span aria-hidden>{emoji}</span>}
		</Cover>
		<Body>
			<CardTitle>{title}</CardTitle>
			<InfoArea>
				{instructorName && <InstructorRow>{instructorName}</InstructorRow>}
				<OverflowRow values={typeLabels} />
				<OverflowRow values={yearLabels} />
				<OverflowRow values={sectionLabels} />
			</InfoArea>
			{!!chips?.length && (
				<ChipsRow>
					{chips.slice(0, 2).map(chip => (
						<button
							key={chip.key}
							type="button"
							onClick={e => {
								e.stopPropagation()
								chip.onClick?.()
							}}
							onKeyDown={e => {
								// The Card also handles Enter/Space; stop it here so keyboard
								// activation follows the chip, not the card's navigation.
								if (e.key === 'Enter' || e.key === ' ') {
									e.stopPropagation()
									e.preventDefault()
									chip.onClick?.()
								}
							}}
						>
							{chip.label}
						</button>
					))}
					{chips.length > 2 && <span className="more" dir="ltr">{`+${chips.length - 2}`}</span>}
				</ChipsRow>
			)}
			<BadgeRow>
				{mode && <Badge tone="gray">{mode}</Badge>}
				{subjectLabel && <Badge tone="blue">{subjectLabel}</Badge>}
				<EndCol>
					{banned ? (
						<Badge tone="error-tint">{bannedLabel}</Badge>
					) : expired ? (
						<Badge tone="gray">{expiredLabel}</Badge>
					) : (
						enrolled && <Badge tone="success-tint">{enrolledLabel}</Badge>
					)}
					{priceText && <Price dir="rtl">{priceText}</Price>}
					{!enrolled && !banned && !expired && ctaLabel && (
						<CtaButton
							type="button"
							onClick={e => {
								e.stopPropagation()
								onClick?.(e)
							}}
						>
							{ctaLabel}
						</CtaButton>
					)}
				</EndCol>
			</BadgeRow>
		</Body>
	</Card>
)

const Card = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--r-xl);
	overflow: hidden;
	box-shadow: var(--sh-rest);
	display: flex;
	flex-direction: column;
	cursor: pointer;
	height: 100%;
	transition: box-shadow var(--dur-fast) var(--ease);
	&:hover {
		box-shadow: var(--sh-hover);
	}
`
/* 2:1 keeps the cover proportional to the card width at every breakpoint. */
const Cover = styled.div`
	aspect-ratio: 2 / 1;
	width: 100%;
	flex: none;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 40px;
	overflow: hidden;
	img.photo {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
	img.icon {
		width: 40px;
		height: 40px;
		object-fit: contain;
	}
`
const Body = styled.div`
	padding: 12px 14px 14px;
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex: 1;
	text-align: start;
`
/* Always exactly two lines tall: short titles reserve the space, long ones
   ellipsize on the second line — so every card body starts level. */
const CardTitle = styled.div`
	font: var(--fw-bold) 13px/1.5 var(--font-sans);
	color: var(--text-primary);
	min-height: calc(2 * 1.5em);
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`
/* Fixed three-row slot (instructor · type · year): cards with missing data
   keep the same height as fully-populated ones, so grids and rails stay level. */
const InfoArea = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-height: calc(3 * 18px + 2 * 6px);
`
const InstructorRow = styled.div`
	height: 18px;
	font: var(--fw-regular) var(--fs-micro-plus)/18px var(--font-sans);
	color: var(--text-secondary);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`
const InfoRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	height: 18px;
	.v {
		font: var(--fw-regular) var(--fs-micro-plus)/18px var(--font-sans);
		color: var(--text-secondary);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.more {
		flex: none;
		font: var(--fw-bold) var(--fs-micro) var(--font-sans);
		color: var(--blue);
		background: var(--blue-08);
		border-radius: var(--r-full);
		padding: 1px 7px;
	}
`
/* Bundle-membership pills — the mockup's yellow chips under the info rows. */
const ChipsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	button {
		border: none;
		cursor: pointer;
		max-width: 100%;
		font: var(--fw-bold) var(--fs-micro) var(--font-sans);
		font-family: var(--font-sans);
		color: var(--yellow-dark, #a16207);
		background: var(--yellow-12, #fef9c3);
		border-radius: var(--r-full);
		padding: 3px 9px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		transition: filter var(--dur-fast) var(--ease);
		&:hover {
			filter: brightness(0.96);
		}
	}
	.more {
		flex: none;
		font: var(--fw-bold) var(--fs-micro) var(--font-sans);
		color: var(--yellow-dark, #a16207);
		background: var(--yellow-12, #fef9c3);
		border-radius: var(--r-full);
		padding: 1px 7px;
	}
`

const BadgeRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	align-items: flex-end;
	margin-top: auto;
	/* Matches the CTA button height so badge-only (enrolled) cards stay level. */
	min-height: 30px;
`
/* End column per the design: badge / price / button stacked, hugging the end. */
const EndCol = styled.div`
	margin-inline-start: auto;
	display: flex;
	flex-direction: column;
	gap: 6px;
	align-items: flex-end;
`
const Price = styled.span`
	font: var(--fw-bold) 13px var(--font-sans);
	color: var(--blue);
	white-space: nowrap;
	/* centered over the CTA button below it (the column's widest child) */
	align-self: center;
	text-align: center;
`
const CtaButton = styled.button`
	border: none;
	border-radius: var(--r-md);
	background: var(--blue);
	color: #fff;
	font: var(--fw-bold) 12px var(--font-sans);
	font-family: var(--font-sans);
	/* Fixed height (not padding-driven) so every container computes the same
	   card height — grids and rails round flexible heights differently. */
	height: 30px;
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	cursor: pointer;
	white-space: nowrap;
	transition: background var(--dur-fast) var(--ease);
	&:hover {
		background: var(--blue-hover);
	}
`
